/**
 * @fileoverview Copies text to the system clipboard from code that runs after
 * an async call, where the Clipboard API often refuses because the click's
 * "user activation" has expired.
 */

/**
 * Copies `text` with `navigator.clipboard.writeText`, falling back to a hidden
 * textarea + `execCommand('copy')`, which still works in some contexts where
 * the Clipboard API rejects.
 *
 * @param text Text to put on the clipboard. Never logged.
 * @return Resolves once copied; rejects when both methods fail.
 */
export function copyToClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    window.focus?.();
    return navigator.clipboard.writeText(text).catch(() => copyWithTextarea(text));
  }
  return copyWithTextarea(text);
}

function copyWithTextarea(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '0';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, text.length);
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (copied) {
        resolve();
      } else {
        reject(new Error('execCommand copy failed'));
      }
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}
